import moment from "moment";
import { BG_COLORS } from "constants";
import { CURRENCY_PREFIX_SPACE_REGEX } from "constants/regexPatterns";
import { SETTINGS_PATHS } from "constants/routes";

export function convertTimezonesToDropdownOptions(value) {
  return value?.map((key) => ({
    label: key.timezone || key.countryCode,
    value: key.timezone || key.countryCode,
  }));
}

export function getOrdinalSuffix(date) {
  if (date >= 11 && date <= 13) {
    return "th";
  }
  const lastDigit = date % 10;
  if (lastDigit === 1) {
    return "st";
  }
  if (lastDigit === 2) {
    return "nd";
  }
  if (lastDigit === 3) {
    return "rd";
  }
  return "th";
}

export function numberToCurrencyFormat(number) {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

export function cleanCurrencyString(str) {
  if (!str) return "";
  const currencyString = str.replace(CURRENCY_PREFIX_SPACE_REGEX, "$1");
  return currencyString;
}

export function getAge(dateString) {
  const today = new Date();
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getBackgroundColor() {
  return BG_COLORS.find((color) => color.path === window.location.pathname)?.color || "#F5F5FF";
}

export function checkIfPadding24Needed() {
  if (window.location.pathname === "/main/addEmployee") return false;

  const currentPath = window.location.pathname;
  if (Object.values(SETTINGS_PATHS).some((path) => currentPath.startsWith(path))) return false;

  return true;
}

export const shouldAddCustomHeightOnCurrentPage = () => {
  if (window.location.pathname === "/main/addEmployee") return false;
  return true;
};

export function getFileSizeToDisplay(fileSizeInBytes) {
  if (fileSizeInBytes > 1024 * 1024) {
    return `${(fileSizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${(fileSizeInBytes / 1024).toFixed(2)} KB`;
}
export const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return d.toISOString().split("T")[0];
};

export const formatPaymentDateMonth = (dateString, includeDay = false) => {
  const [year, month, day] = dateString.split("-");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const formattedMonth = monthNames[parseInt(month, 10) - 1];

  return includeDay ? `${formattedMonth} ${parseInt(day, 10)}, ${year}` : `${formattedMonth}, ${year}`;
};

export const getNationalIDHelperText = (country) => {
  if (country === "Pakistan") return "Enter 13-character CNIC number";
  if (country === "Peru") return "Enter 9-character DNI number";
  if (country === "United States") return "Enter 9-character SSN number";

  return "";
};

export const getUserRole = (userItem) => {
  const permission = userItem?.permissions?.[0];

  if (permission === "EdgeAdmin") {
    return "Admin";
  }

  if (permission === "CustomerAdmin" && userItem.mainUser === true) {
    return "Account Owner";
  }

  if (permission === "CustomerAdmin") {
    return "Customer Admin";
  }

  if (permission === "EdgeEmployee") {
    return "Employee";
  }

  if (permission === "CustomerEmployee") {
    return "Customer User";
  }

  if (permission === "CustomerViewer") {
    return "Customer Viewer";
  }

  return permission;
};

export const getDocumentPermissions = (permissions) => {
  if (!permissions || !permissions.enabled) return false;

  const allowedRoles = permissions.allowedRoles || [];
  const userPermissions = JSON.parse(localStorage.getItem("loggedInUser"))?.permissions || [];
  return userPermissions.some((permission) => allowedRoles.includes(permission));
};

export function pluralize(word, count, pluralForm) {
  const plural = pluralForm || `${word}s`;
  return count === 1 ? word : plural;
}

export const hasOnScreenNavigation = () => {
  const screenHeight = window.screen.height;
  const windowHeight = window.innerHeight;
  return screenHeight > windowHeight;
};

export const getChipStatusColor = (statusType) => {
  switch (statusType?.toLowerCase()) {
    case "permanent":
      return "success";
    case "probation":
      return "warning";
    case "notice_period":
      return "error";
    default:
      return "default";
  }
};

export const getFieldLabels = ({ selectedStatus }) => {
  switch (selectedStatus) {
    case "Probation":
      return { startLabel: "Probation start date", endLabel: "Probation end date" };
    case "Permanent":
      return { startLabel: "Permanent start date", endLabel: null };
    case "Notice Period":
      return { startLabel: "Notice period start date", endLabel: "Notice period end date" };
    default:
      return { startLabel: "Start date", endLabel: "End date" };
  }
};

export const hasExtension = (fileName, allowedExtensions = [".pdf", ".png", ".jpeg"]) =>
  allowedExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));

export const formatDateAsLocal = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getDaysDifferenceBetweenDates = (startDate, endDate) => {
  const timeDifferenceInMilliseconds = new Date(endDate) - new Date(startDate);
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil(timeDifferenceInMilliseconds / millisecondsPerDay);
};

export function formatDateWithTimeMs(date) {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n, z = 2) => `${n}`.padStart(z, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}
export const getStorageItem = ({ key, parse = false }) => {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      return parse ? JSON.parse(item) : item;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const findNearestSaturday = () => {
  const today = moment();
  const dayOfWeek = today.day();
  if (dayOfWeek === 6) {
    return today.toDate();
  }
  if (dayOfWeek === 0) {
    return today.subtract(1, "day").toDate();
  }
  const daysUntilSaturday = 6 - dayOfWeek;
  return today.add(daysUntilSaturday, "days").toDate();
};

export const isWeekend = (date) => {
  const dayOfWeek = moment(date).day();
  return dayOfWeek === 0 || dayOfWeek === 6;
};
